import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, Clock, Calendar, Leaf } from "lucide-react";
import { AnimatedSection, API } from "./shared";
import { trackLead } from "../utils/tracking";

const FOOD_IMAGE = "https://images.unsplash.com/photo-1556911073-52527ac43761?auto=format&fit=crop&w=800&q=80";

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", age: "", gender: "", health_issues: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      const whatsappPhone = "919108818369";
      const msg = `New Enquiry from Website:\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}${formData.age ? `\nAge: ${formData.age}` : ""}${formData.gender ? `\nGender: ${formData.gender}` : ""}${formData.health_issues ? `\nHealth Concerns: ${formData.health_issues}` : ""}`;
      window.open(`https://wa.me/${whatsappPhone}?text=${encodeURIComponent(msg)}`, "_blank");
      setSubmitStatus({ type: "success", message: response.data.message || "Message sent! Redirecting to WhatsApp." });
      trackLead({ content_name: "Contact Form", value: formData.health_issues });
      setFormData({ name: "", email: "", phone: "", age: "", gender: "", health_issues: "" });
    } catch (error) {
      setSubmitStatus({ type: "error", message: error.response?.data?.detail || "Something went wrong." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full bg-[#FEFDFB] border border-[#0D1F17]/[0.08] px-4 py-3 text-[#0D1F17] placeholder-[#5A6B5E]/50 focus:border-[#1A3C2A] focus:ring-1 focus:ring-[#1A3C2A]/20 focus:outline-none transition-colors rounded-lg text-sm";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #F0F5F1 0%, #F7F5F0 50%, #F5F9F6 100%)" }}
    >
      {/* Left side HD food border */}
      <div className="absolute left-0 top-0 bottom-0 w-[260px] hidden xl:block pointer-events-none">
        <img src="https://images.unsplash.com/photo-1690983322070-22861e13ce47?auto=format&fit=crop&w=600&h=1200&q=80" alt="" className="w-full h-full object-cover opacity-[0.12]" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#F3F3EE]" />
      </div>
      {/* Right side HD food border */}
      <div className="absolute right-0 top-0 bottom-0 w-[260px] hidden xl:block pointer-events-none">
        <img src="https://images.unsplash.com/photo-1572357176061-7c96fd2af22f?auto=format&fit=crop&w=600&h=1200&q=80" alt="" className="w-full h-full object-cover opacity-[0.12]" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#F3F3EE]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left side — info + food image */}
          <AnimatedSection>
            <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37]">
              <Leaf size={12} />
              Get in Touch
            </span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-[#0D1F17] mt-3 mb-6">
              Let&apos;s Start Your <span className="italic font-light">Journey</span>
            </h2>
            <p className="text-[#5A6B5E] text-lg leading-relaxed mb-8">
              Fill out the form and our team will get back to you within 24 hours. Or book a consultation to discuss your health goals.
            </p>

            {/* Food image accent */}
            <div className="relative rounded-2xl overflow-hidden mb-8 shadow-lg hidden md:block" data-testid="contact-food-image">
              <img
                src={FOOD_IMAGE}
                alt="Healthy cooking with fresh fruits and ingredients"
                className="w-full h-48 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0D1F17]/60 to-transparent" />
              <div className="absolute bottom-4 left-5">
                <p className="font-serif text-lg text-[#FEFDFB] italic">Nutrition-first wellness</p>
                <p className="text-[#D4AF37] text-[10px] tracking-[0.15em] uppercase font-semibold mt-0.5">
                  Evidence-based approach
                </p>
              </div>
            </div>

            <div className="space-y-5">
              {[
                [Phone, "Call Us", "+91 91088 18369", "tel:+919108818369"],
                [Mail, "Email Us", "fitlyindiahealth@gmail.com", "mailto:fitlyindiahealth@gmail.com"],
                [Clock, "Working Hours", "Mon - Sat, 10 AM - 6 PM", null],
              ].map(([Icon, label, value, href]) => (
                <div key={label} className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#0D1F17]/[0.06] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4.5 h-4.5 text-[#0D1F17]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#5A6B5E] uppercase tracking-[0.15em]">{label}</p>
                    {href ? (
                      <a href={href} className="text-[#0D1F17] hover:text-[#D4AF37] transition-colors text-sm font-medium">{value}</a>
                    ) : (
                      <p className="text-[#0D1F17] text-sm font-medium">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <a
                href="/consultation"
                data-testid="contact-book-call-btn"
                className="bg-[#D4AF37] text-[#0D1F17] px-8 py-4 text-sm tracking-wide font-semibold hover:bg-[#E8D48B] transition-all inline-flex items-center gap-2 rounded-full"
              >
                <Calendar className="w-4 h-4" /> Book Consultation
              </a>
            </div>
          </AnimatedSection>

          {/* Right side — form */}
          <AnimatedSection delay={0.2}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white/80 backdrop-blur-sm border border-[#0D1F17]/[0.06] p-8 rounded-2xl space-y-4 shadow-lg shadow-[#0D1F17]/[0.03]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Your Name *"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="contact-name"
                  className={inputClass}
                />
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contact-email"
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="tel"
                  placeholder="Phone *"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contact-phone"
                  className={inputClass}
                />
                <input
                  type="number"
                  placeholder="Age"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  data-testid="contact-age"
                  className={inputClass}
                />
              </div>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                data-testid="contact-gender"
                className={inputClass}
              >
                <option value="">Select Gender</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="couple">Couple</option>
              </select>
              <textarea
                placeholder="Your health concerns..."
                rows={4}
                value={formData.health_issues}
                onChange={(e) => setFormData({ ...formData, health_issues: e.target.value })}
                data-testid="contact-health"
                className={`${inputClass} resize-none`}
              />
              {submitStatus && (
                <div
                  className={`p-4 rounded-lg text-sm ${
                    submitStatus.type === "success"
                      ? "bg-[#0D1F17]/5 text-[#0D1F17] border border-[#0D1F17]/20"
                      : "bg-red-50 text-red-600 border border-red-200"
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="w-full bg-[#0D1F17] text-[#FEFDFB] py-4 text-sm tracking-wide font-medium hover:bg-[#1A3C2A] transition-all disabled:opacity-50 rounded-full"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
